import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

/**
 * Floating circular button that appears after the user scrolls past a threshold.
 * Smoothly scrolls to top on click (or instant when prefers-reduced-motion).
 * Hides on portal/admin/auth chrome routes to match Navbar visibility rules.
 */
const HIDE_PREFIXES = ["/portal", "/signup", "/login", "/verify"];

export default function ScrollToTopButton({ threshold = 400 }) {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  const hidden =
    (pathname.startsWith("/admin") && pathname !== "/admin/login") ||
    HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (hidden) return null;

  const handleClick = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, left: 0, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll to top"
      data-testid="scroll-to-top-btn"
      className={`fixed bottom-6 right-6 z-[80] w-12 h-12 rounded-full bg-electric text-black flex items-center justify-center shadow-lg shadow-electric/40 hover:shadow-electric/60 hover:-translate-y-1 transition-all duration-300 group ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      {/* Pulsing ring */}
      <span className="absolute inset-0 rounded-full bg-electric animate-ping opacity-20 pointer-events-none" aria-hidden="true" />
      <ArrowUp className="w-5 h-5 relative group-hover:-translate-y-0.5 transition-transform" strokeWidth={2.5} />
    </button>
  );
}
